import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/useAuth';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight,
  BadgeCheck,
  BookHeart,
  FileDown,
  Gift,
  Library,
  Loader2,
  Printer,
  RefreshCw,
  ScanFace,
  ShieldCheck,
  Sparkles,
  Truck,
  UserRoundCheck,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { StorybookShell } from '@/components/storybook/StorybookShell';
import { StorybookBuilder } from '@/components/storybook/StorybookBuilder';
import { StorybookPriceTag } from '@/components/storybook/StorybookPriceTag';
import { StorybookHeroVisual } from '@/components/storybook/marketing/StorybookHeroVisual';
import { PhotoToStorySection } from '@/components/storybook/marketing/PhotoToStorySection';
import { FamilySection } from '@/components/storybook/marketing/FamilySection';
import { ApproveSection } from '@/components/storybook/marketing/ApproveSection';
import { HowItWorksSection } from '@/components/storybook/marketing/HowItWorksSection';
import { SectionHeading } from '@/components/storybook/marketing/SectionHeading';
import { PROOF_REDRAW_ROUNDS } from '@/components/storybook/marketing/promo';
import { trackEvent, trackPageView } from '@/lib/analytics';
import {
  getStorybookConfig,
  listSavedStorybooks,
  type SavedStorybook,
  type StorybookConfig,
} from '@/lib/storybook-api';
import { formatCountryList, formatStorybookPrice, storybookTotalWithShipping } from '@/lib/storybook-utils';
import { ApprovalRail } from '@/components/trust/ApprovalRail';
import { CheckoutStrip } from '@/components/trust/CheckoutStrip';
import { SystemChecks } from '@/components/trust/SystemChecks';
import { SupportLine } from '@/components/trust/SupportLine';
import { ShareToolBar } from '@/components/share/ShareToolBar';
import { cn } from '@/lib/utils';

type ConfigState = 'loading' | 'ready' | 'error';

// Until GET /config answers, the marketing describes what the backend
// offers by default (services/storybook/config.js): 6 photos, 2 people
// joining the child, 3 free cover redraws. Prices are never defaulted: the
// pricing section waits for /config.
const DEFAULT_MAX_PHOTOS = 6;
const DEFAULT_MAX_COMPANIONS = 2;
const DEFAULT_COVER_REDRAWS = 3;

const HERO_CHIPS = [
  { key: 'freePreview', icon: Gift, tint: 'bg-[hsl(45_95%_85%)] text-[hsl(30_70%_28%)]' },
  { key: 'realFaces', icon: ScanFace, tint: 'bg-[hsl(330_85%_92%)] text-[hsl(330_60%_38%)]' },
  { key: 'approve', icon: BadgeCheck, tint: 'bg-[hsl(150_55%_90%)] text-[hsl(160_60%_24%)]' },
  { key: 'noAccount', icon: UserRoundCheck, tint: 'bg-secondary text-secondary-foreground' },
] as const;

const FAQ_KEYS = ['time', 'photos', 'family', 'approval', 'privacy', 'notHappy', 'languages', 'shipping', 'delete'] as const;

const PDF_FEATURES = ['illustrations', 'faces', 'story', 'approve', 'download'] as const;

function scrollToId(id: string) {
  document.getElementById(id)?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
}

function formatSavedDate(iso: string, language: string): string {
  const date = new Date(iso);
  if (!iso || Number.isNaN(date.getTime())) return '';
  try {
    return date.toLocaleDateString(language, { year: 'numeric', month: 'short', day: 'numeric' });
  } catch {
    return date.toDateString();
  }
}

export default function Storybook() {
  const { t, i18n } = useTranslation();
  const { isAuthenticated: signedIn, isLoading: authLoading } = useAuth();
  const location = useLocation();
  const [config, setConfig] = useState<StorybookConfig | null>(null);
  const [configState, setConfigState] = useState<ConfigState>('loading');
  const [reloadKey, setReloadKey] = useState(0);
  const [saved] = useState<SavedStorybook[]>(() => listSavedStorybooks());
  const [faqOpen, setFaqOpen] = useState('');
  const scrolledToHash = useRef(false);

  useEffect(() => {
    trackPageView('storybook');
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    setConfigState('loading');
    getStorybookConfig(controller.signal)
      .then((next) => {
        setConfig(next);
        setConfigState('ready');
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.warn('[storybook] config failed', error instanceof Error ? error.message : error);
        setConfigState('error');
      });
    return () => controller.abort();
  }, [reloadKey]);

  // "Start a new story" links from the book page land on /storybook#builder.
  useEffect(() => {
    if (scrolledToHash.current || configState === 'loading') return;
    const target = location.hash.replace('#', '');
    if (!target) return;
    scrolledToHash.current = true;
    window.setTimeout(() => scrollToId(target), 50);
  }, [configState, location.hash]);

  const ready = configState === 'ready' && !!config;
  const enabled = ready && config.enabled === true;
  const printEnabled = enabled && config.printEnabled === true;
  // Print is mentioned until /config says it can't be ordered (as before).
  const printShown = !(configState === 'ready' && !printEnabled);
  const countries = formatCountryList(config?.printCountries || [], i18n.language);
  const shipping = config?.prices?.shipping;
  const paidShipping = shipping && shipping.cents > 0 ? formatStorybookPrice(shipping) : null;
  // What Stripe Checkout will total once it adds the shipping line (null: shipping is free).
  const printTotal = config?.prices?.print ? storybookTotalWithShipping(config.prices.print, shipping) : null;
  const upgradeTotal = config?.prices?.printUpgrade ? storybookTotalWithShipping(config.prices.printUpgrade, shipping) : null;

  // What the builder will actually offer, the same way it reads /config.
  const maxPhotos = ready ? Math.max(0, config.limits?.maxPhotos ?? 0) : DEFAULT_MAX_PHOTOS;
  const photosOffered = maxPhotos > 0;
  const relations = ready ? (Array.isArray(config.relations) ? config.relations : []) : null;
  const maxCompanions = ready ? Math.min(2, Math.max(0, config.cast?.maxCompanions ?? 1)) : DEFAULT_MAX_COMPANIONS;
  const familyOffered = (relations === null || relations.length > 0) && maxCompanions > 0;
  const coverRedraws = ready ? Math.max(0, config.redraws?.max ?? 0) : DEFAULT_COVER_REDRAWS;

  const startStory = (source: string) => {
    trackEvent('storybook_cta_clicked', { source });
    scrollToId('builder');
  };

  const openPrivacyFaq = () => {
    setFaqOpen('privacy');
    scrollToId('storybook-faq');
  };

  // The first screen promises door delivery, so it also says where printed
  // books ship (printCountries from /config) as soon as /config has answered.
  const heroIncludes = [
    t('storybookPage.hero.bullets.illustrations'),
    t('storybookPage.hero.bullets.likeness'),
    ...(printShown
      ? [countries ? t('storybookPage.hero.bullets.printTo', { countries }) : t('storybookPage.hero.bullets.print')]
      : []),
  ].join(' · ');

  const faqKeys = FAQ_KEYS.filter((key) => {
    if (key === 'shipping') return printShown;
    if (key === 'photos') return photosOffered;
    if (key === 'family') return familyOffered;
    return true;
  });

  const faqAnswer = (key: (typeof FAQ_KEYS)[number]) => {
    const values = {
      countries: countries || t('storybookPage.faq.defaultCountries'),
      ...paidShipping,
      max: maxPhotos,
      rounds: PROOF_REDRAW_ROUNDS,
      redraws: coverRedraws,
    };
    if (key === 'shipping') return t(`storybookPage.faq.items.shipping.${paidShipping ? 'aPaid' : 'a'}`, values);
    if (key === 'notHappy') return t(`storybookPage.faq.items.notHappy.${coverRedraws > 0 ? 'a' : 'aNoRedraw'}`, values);
    if (key === 'family') return t('storybookPage.faq.items.family.a', { ...values, count: maxCompanions });
    return t(`storybookPage.faq.items.${key}.a`, values);
  };

  return (
    <StorybookShell title={`${t('storybookPage.meta.title')} | FaceShot-ChopShop`}>
      {/* HERO: the photo becomes the storybook, at a glance. On a phone the
          picture sits right under the headline, before the longer copy. */}
      <section className="relative overflow-hidden" aria-labelledby="storybook-hero-title">
        <div aria-hidden="true" className="pointer-events-none absolute inset-0">
          <div className="absolute -left-24 -top-24 h-72 w-72 rounded-full bg-[hsl(199_90%_85%)] opacity-60 blur-3xl" />
          <div className="absolute -right-16 top-10 h-72 w-72 rounded-full bg-[hsl(330_85%_88%)] opacity-60 blur-3xl" />
          <div className="absolute bottom-0 left-1/3 h-64 w-64 rounded-full bg-[hsl(45_95%_78%)] opacity-50 blur-3xl" />
        </div>
        <div className="container relative mx-auto grid items-center gap-x-12 gap-y-6 px-4 pb-10 pt-6 sm:gap-y-8 sm:py-16 lg:grid-cols-[1.05fr_0.95fr] lg:grid-rows-[auto_1fr] lg:py-20">
          <div className="text-center lg:col-start-1 lg:row-start-1 lg:self-end lg:text-left">
            <span className="inline-flex items-center gap-2 rounded-full border border-border bg-card/80 px-4 py-1.5 text-sm font-semibold text-primary shadow-sm">
              <Sparkles className="h-4 w-4" aria-hidden="true" />
              {t('storybookPage.hero.eyebrow')}
            </span>
            {/* Both of the owner's points at first glance (2026-09-28): the
                pictures are drawn from YOUR photos to look like your child,
                and the book can be ordered printed. On a phone the picture
                pushes the subtitle below the fold, so "printed" rides in the
                headline itself — and only while print can be ordered. As an
                option ("Order it printed"), never "a printed storybook": the
                PDF is the other choice. Each sentence gets its own line and
                wraps evenly, so no word is left alone on a phone. */}
            <h1 id="storybook-hero-title" className="mt-5 text-4xl font-semibold leading-[1.1] [text-wrap:balance] sm:text-5xl lg:text-6xl">
              {t('storybookPage.hero.title')}{' '}
              <br />
              <span className="gradient-text inline-block max-w-full [text-wrap:balance]">
                {t(printShown ? 'storybookPage.hero.titleHighlightPrint' : 'storybookPage.hero.titleHighlight')}
              </span>
            </h1>
          </div>

          <div className="lg:col-start-2 lg:row-span-2 lg:row-start-1">
            <StorybookHeroVisual />
          </div>

          <div className="text-center lg:col-start-1 lg:row-start-2 lg:self-start lg:text-left">
            <p className="mx-auto max-w-xl text-lg text-muted-foreground lg:mx-0">
              {t(printShown ? 'storybookPage.hero.subtitlePrint' : 'storybookPage.hero.subtitle')}
            </p>
            <ShareToolBar path="/storybook" className="mt-5 items-center lg:items-start" />
            <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center lg:justify-start">
              <Button size="xl" className="w-full rounded-full sm:w-auto h-auto min-h-14 whitespace-normal py-3 text-center leading-snug" onClick={() => startStory('storybook_hero')}>
                <BookHeart aria-hidden="true" />
                {t('storybookPage.hero.cta')}
              </Button>
              <Button variant="outline" size="xl" className="w-full rounded-full bg-card/70 sm:w-auto h-auto min-h-14 whitespace-normal py-3 text-center leading-snug" onClick={() => scrollToId('examples')}>
                {t('storybookPage.hero.secondaryCta')}
              </Button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">{heroIncludes}</p>
            <ul className="mx-auto mt-6 grid max-w-xl grid-cols-2 gap-2 text-left sm:flex sm:flex-wrap sm:justify-center lg:mx-0 lg:justify-start">
              {HERO_CHIPS.map((chip) => (
                <li key={chip.key} className="flex items-center gap-2 rounded-2xl bg-card/80 px-2.5 py-2 text-sm font-semibold leading-tight shadow-sm sm:rounded-full sm:pr-3.5">
                  <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full', chip.tint)}>
                    <chip.icon className="h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                  {t(`storybookPage.hero.chips.${chip.key}`)}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* SAVED BOOKS (this device) */}
      {saved.length > 0 && (
        <section className="pb-4" aria-labelledby="storybook-saved-title">
          <div className="container mx-auto px-4">
            <div className="mx-auto max-w-4xl rounded-3xl border border-border bg-card p-5 shadow-[var(--shadow-card)] sm:p-6">
              <div className="mb-4 flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-secondary text-secondary-foreground">
                  <Library className="h-5 w-5" aria-hidden="true" />
                </span>
                <div>
                  <h2 id="storybook-saved-title" className="text-xl font-semibold">{t('storybookPage.saved.title')}</h2>
                  <p className="text-sm text-muted-foreground">{t('storybookPage.saved.subtitle')}</p>
                </div>
              </div>
              <ul className="grid gap-3 sm:grid-cols-2">
                {saved.map((book, index) => {
                  const date = formatSavedDate(book.createdAt, i18n.language);
                  return (
                    <li key={book.id}>
                      <Link
                        to={`/storybook/${encodeURIComponent(book.id)}`}
                        className="group flex items-center gap-3 rounded-2xl border border-border bg-background px-4 py-3 transition-colors hover:border-primary/60 hover:bg-primary/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        <span aria-hidden="true" className="text-2xl">{['📘', '📗', '📙', '📕'][index % 4]}</span>
                        <span className="min-w-0 flex-1">
                          <span className="block font-semibold">{t('storybookPage.saved.item', { n: saved.length - index })}</span>
                          {date && (
                            <span className="block text-xs text-muted-foreground">{t('storybookPage.saved.started', { date })}</span>
                          )}
                        </span>
                        <span className="flex items-center gap-1 text-sm font-semibold text-primary">
                          {t('storybookPage.saved.open')}
                          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
                        </span>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        </section>
      )}

      <PhotoToStorySection onPrivacyDetails={openPrivacyFaq} />

      {/* No style section: every book is drawn in the 3D style (owner, 2026-09-29), so there is nothing to pick. */}

      {familyOffered && <FamilySection relations={relations} maxCompanions={maxCompanions} maxPhotos={maxPhotos} />}

      <ApproveSection print={printShown} />

      <HowItWorksSection
        photos={photosOffered}
        print={printShown}
        coverRedraws={coverRedraws}
        onStart={() => startStory('storybook_how')}
      />

      {/* BUILDER */}
      <section id="builder" className="scroll-mt-20 py-16 sm:py-20" aria-labelledby="storybook-builder-title">
        <div className="container mx-auto px-4">
          <SectionHeading
            id="storybook-builder-title"
            eyebrow={t('storybookPage.builder.eyebrow')}
            title={t('storybookPage.builder.title')}
            subtitle={t('storybookPage.builder.subtitle')}
          />
          {/* The approval rail as the builder's progress header (Studio Blueprint 05-B, 2026-10-16). */}
          {enabled && <ApprovalRail kind="storybook" title={false} className="mb-10" />}
          <div className="mx-auto max-w-4xl">
            {configState === 'loading' && (
              <div role="status" className="glass-card flex items-center justify-center gap-3 p-10 text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin text-primary" aria-hidden="true" />
                {t('storybookPage.builder.loadingConfig')}
              </div>
            )}
            {configState === 'error' && (
              <div role="alert" className="glass-card p-8 text-center">
                <p className="text-4xl" aria-hidden="true">🙈</p>
                <p className="mt-3 font-semibold">{t('storybookPage.builder.configError')}</p>
                <Button variant="outline" className="mt-5 rounded-full" onClick={() => setReloadKey((key) => key + 1)}>
                  <RefreshCw aria-hidden="true" />
                  {t('storybookPage.builder.retry')}
                </Button>
              </div>
            )}
            {configState === 'ready' && config && !config.enabled && (
              <div data-testid="storybook-launching-soon" className="glass-card overflow-hidden p-8 text-center sm:p-12">
                <p className="text-5xl" aria-hidden="true">🌱📚✨</p>
                <span className="mt-5 inline-block rounded-full bg-accent px-3 py-1 text-xs font-bold uppercase tracking-wider text-accent-foreground">
                  {t('storybookPage.launching.badge')}
                </span>
                <h3 className="mt-3 text-2xl font-semibold sm:text-3xl">{t('storybookPage.launching.title')}</h3>
                <p className="mx-auto mt-3 max-w-lg text-muted-foreground">{t('storybookPage.launching.body')}</p>
              </div>
            )}
            {/* Registered first (owner 2026-10-27: "a user shouldnt be able to use our tools without registering on our
                site first. that would eliminate the issue that a user lost their entire creation"): signed out, the builder
                is a sign-up / log-in card that comes back here. A gift's parents use their own link (StorybookGiftRedeem). */}
            {enabled && config && (signedIn ? <StorybookBuilder config={config} /> : !authLoading && (
              <div data-testid="storybook-sign-in-first" className="glass-card p-8 text-center sm:p-10">
                <h3 className="text-2xl font-semibold">{t('qt.signInFirst.title')}</h3>
                <p className="mx-auto mt-3 max-w-lg text-muted-foreground">{t('qt.signInFirst.hint')}</p>
                <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                  <Button asChild size="lg"><Link to={`/signup?redirect_to=${encodeURIComponent('/storybook#builder')}`} onClick={() => trackEvent('storybook_sign_in_first', { mode: 'signup' })}>{t('qt.signInFirst.signup')}</Link></Button>
                  <Button asChild size="lg" variant="outline"><Link to={`/login?redirect_to=${encodeURIComponent('/storybook#builder')}`} onClick={() => trackEvent('storybook_sign_in_first', { mode: 'login' })}>{t('qt.signInFirst.login')}</Link></Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* No "Peek inside" spreads (2026-09-28): the site shows only the one
          drawing the owner approved, which is a cover, and a cover shown
          as a story page would say something untrue. */}

      {/* PRICING */}
      {enabled && config && (
        <section id="pricing" className="scroll-mt-20 py-16 sm:py-20" aria-labelledby="storybook-pricing-title">
          <div className="container mx-auto px-4">
            <SectionHeading
              id="storybook-pricing-title"
              eyebrow={t('storybookPage.pricing.eyebrow')}
              title={t('storybookPage.pricing.title')}
              subtitle={t('storybookPage.pricing.subtitle')}
            />
            <div className={cn('mx-auto grid gap-6', printEnabled ? 'max-w-4xl md:grid-cols-2' : 'max-w-md')}>
              <div className="glass-card flex flex-col p-6 sm:p-8" data-testid="storybook-price-pdf">
                <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-secondary text-secondary-foreground">
                  <FileDown className="h-6 w-6" aria-hidden="true" />
                </span>
                <h3 className="mt-4 text-2xl font-semibold">{t('storybookPage.pricing.pdf.name')}</h3>
                <p className="mt-1 text-sm text-muted-foreground">{t('storybookPage.pricing.pdf.desc')}</p>
                <StorybookPriceTag className="mt-4" cents={config.prices.pdf.cents} usdCents={config.prices.pdf.usdCents} />
                <ul className="mt-5 flex-1 space-y-2 text-sm">
                  {PDF_FEATURES.map((key) => (
                    <li key={key} className="flex gap-2">
                      <span aria-hidden="true" className="text-primary">✓</span>
                      {/* "From the photos you upload" only while the builder takes photos. */}
                      {t(`storybookPage.pricing.pdf.features.${key === 'faces' && photosOffered ? 'facesPhotos' : key}`)}
                    </li>
                  ))}
                </ul>
                <Button variant="outline" size="lg" className="mt-6 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug" onClick={() => startStory('storybook_pricing_pdf')}>
                  {t('storybookPage.pricing.cta')}
                </Button>
              </div>

              {printEnabled && (
                <div className="glass-card relative flex flex-col border-primary p-6 shadow-[0_0_0_1px_hsl(var(--primary)),var(--shadow-card)] sm:p-8" data-testid="storybook-price-print">
                  <span className="absolute -top-3 left-6 inline-flex items-center gap-1 rounded-full bg-primary px-3 py-0.5 text-xs font-bold uppercase tracking-wider text-primary-foreground">
                    <Gift className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('storybookPage.pricing.print.badge')}
                  </span>
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[hsl(45_95%_85%)] text-[hsl(30_70%_28%)]">
                    <Printer className="h-6 w-6" aria-hidden="true" />
                  </span>
                  <h3 className="mt-4 text-2xl font-semibold">{t('storybookPage.pricing.print.name')}</h3>
                  <p className="mt-1 text-sm text-muted-foreground">{t('storybookPage.pricing.print.desc')}</p>
                  <StorybookPriceTag className="mt-4" cents={config.prices.print.cents} usdCents={config.prices.print.usdCents} />
                  {printTotal && (
                    <p className="mt-2 text-sm font-semibold" data-testid="storybook-price-print-total">
                      {t('storybookPage.pricing.print.totalWithShipping', printTotal)}
                    </p>
                  )}
                  <ul className="mt-5 flex-1 space-y-2 text-sm">
                    {(['f1', 'f2', 'approve'] as const).map((key) => (
                      <li key={key} className="flex gap-2">
                        <span aria-hidden="true" className="text-primary">✓</span>
                        {t(`storybookPage.pricing.print.features.${key}`)}
                      </li>
                    ))}
                    <li className="flex gap-2">
                      <Truck className="mt-0.5 h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
                      {paidShipping
                        ? t('storybookPage.pricing.print.features.shippingPaid', { countries, ...paidShipping })
                        : t('storybookPage.pricing.print.features.shipping', { countries })}
                    </li>
                  </ul>
                  <p className="mt-4 rounded-xl bg-muted px-3 py-2 text-xs text-muted-foreground" data-testid="storybook-price-upgrade">
                    {upgradeTotal
                      ? t('storybookPage.pricing.print.upgradeNotePaid', {
                        ...formatStorybookPrice(config.prices.printUpgrade),
                        totalMxn: upgradeTotal.mxn,
                        totalUsd: upgradeTotal.usd,
                      })
                      : t('storybookPage.pricing.print.upgradeNote', formatStorybookPrice(config.prices.printUpgrade))}
                  </p>
                  <Button size="lg" className="mt-6 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug" onClick={() => startStory('storybook_pricing_print')}>
                    {t('storybookPage.pricing.cta')}
                  </Button>
                </div>
              )}
            </div>
            {/* Gift a storybook (2026-10-16): someone else's family pays the same price. */}
            <div className="mx-auto mt-8 flex max-w-4xl flex-col items-center gap-4 rounded-3xl bg-[linear-gradient(135deg,hsl(45_95%_88%),hsl(330_85%_92%)_55%,hsl(199_90%_90%))] p-6 text-center sm:flex-row sm:p-7 sm:text-left" data-testid="storybook-gift-band">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/80 text-primary">
                <Gift className="h-6 w-6" aria-hidden="true" />
              </span>
              <span className="flex-1">
                <span className="block text-lg font-semibold">{t('storybookGift.entry.title')}</span>
                <span className="block text-sm text-foreground/80">{t('storybookGift.entry.body')}</span>
              </span>
              <Button asChild size="lg" className="h-auto min-h-12 w-full whitespace-normal rounded-full py-3 sm:w-auto">
                <Link to="/storybook/gift" onClick={() => trackEvent('storybook_cta_clicked', { source: 'storybook_gift_band' })}>
                  <Gift aria-hidden="true" />
                  {t('storybookGift.entry.cta')}
                </Link>
              </Button>
            </div>
            <p className="mx-auto mt-6 max-w-2xl text-center text-sm font-medium" data-testid="storybook-pay-when">
              {t('storybookPage.pricing.payWhen')}
            </p>
            <p className="mx-auto mt-2 max-w-2xl text-center text-xs leading-relaxed text-muted-foreground">
              {t('storybookPage.pricing.totalAtCheckout')}
            </p>
            {/* How people can pay, before the Stripe page (Studio Blueprint 05-E, 2026-10-16). */}
            <CheckoutStrip methods="storybook" className="mt-4" />
          </div>
        </section>
      )}

      {/* FAQ */}
      <section id="storybook-faq" className="scroll-mt-20 py-16 sm:py-20" aria-labelledby="storybook-faq-title">
        <div className="container mx-auto px-4">
          <SectionHeading id="storybook-faq-title" eyebrow={t('storybookPage.faq.eyebrow')} title={t('storybookPage.faq.title')} />
          <Accordion type="single" collapsible value={faqOpen} onValueChange={setFaqOpen} className="mx-auto max-w-3xl space-y-3">
            {faqKeys.map((key) => (
              <AccordionItem key={key} value={key} className="glass-card border-none px-5 sm:px-6">
                <AccordionTrigger className="py-5 text-left hover:no-underline">
                  <span className="font-semibold">{t(`storybookPage.faq.items.${key}.q`)}</span>
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-base leading-relaxed text-muted-foreground">
                  {faqAnswer(key)}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          {/* System checks and a person to ask (Studio Blueprint 05-C/F, 2026-10-16). */}
          <div className="mx-auto mt-10 max-w-3xl space-y-6">
            <SystemChecks checks={['storybookFaces', 'cardsToStripe']} />
            <SupportLine where="storybook" />
          </div>
          <div className="mx-auto mt-10 flex max-w-3xl flex-col items-center gap-3 rounded-3xl bg-[linear-gradient(135deg,hsl(45_95%_88%),hsl(330_85%_92%)_55%,hsl(199_90%_90%))] p-8 text-center">
            <ShieldCheck className="h-8 w-8 text-[hsl(160_60%_28%)]" aria-hidden="true" />
            <p className="max-w-xl text-sm leading-relaxed text-foreground">{t('storybookPage.faq.privacyPromise')}</p>
            {enabled && (
              <Button size="lg" className="mt-2 rounded-full h-auto min-h-12 whitespace-normal py-3 text-center leading-snug" onClick={() => startStory('storybook_faq')}>
                <BookHeart aria-hidden="true" />
                {t('storybookPage.hero.cta')}
              </Button>
            )}
          </div>
        </div>
      </section>
    </StorybookShell>
  );
}
